import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import API from '../utils/api';

export const INVITE_UNLOCK_KEY = 'cherrypkd_invite_unlocked';

export function hasInviteUnlock() {
  try {
    if (localStorage.getItem(INVITE_UNLOCK_KEY) === '1') return true;
  } catch { /* private mode */ }
  try {
    return document.cookie.split(';').some((c) => c.trim().startsWith('invite_unlocked=1'));
  } catch {
    return false;
  }
}

export function setInviteUnlock() {
  try { localStorage.setItem(INVITE_UNLOCK_KEY, '1'); } catch { /* ignore */ }
}

// UX review: the invite-request address is shown as text too, for visitors without a mail app.
const INVITE_EMAIL = 'support@cherrypkd.com';

// UX review: helper and footer text use rgba(255,255,255,.72): >= 7.08:1 on the splash background
// (#1A1A1A under the strongest red glow), 9.45:1 on plain #1A1A1A. Previously .4 / .5 (3.25:1 / 4.23:1).
const HELPER_COLOR = 'rgba(255,255,255,.72)';

// QA r5: footer links get a >= 40px tap area (same 12px text).
const FOOTER_TAP = { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minHeight: 40, minWidth: 40 };

function codeFromLocation(pathname, search) {
  const q = new URLSearchParams(search).get('invite');
  if (q) return q.trim();
  const m = pathname.match(/^\/invite\/([^/?#]+)/i);
  if (m) return decodeURIComponent(m[1]).trim();
  return '';
}

/**
 * Soft-launch invite splash. Shown when INVITE_GATE_ENABLED and visitor
 * is not unlocked and not logged in. Optional onBack returns to the public
 * landing page.
 */
export default function InviteSplash({ onUnlocked, onBack, initialCode = '' }) {
  const location = useLocation();
  const navigate = useNavigate();
  const urlCode = useMemo(
    () => initialCode || codeFromLocation(location.pathname, location.search),
    [initialCode, location.pathname, location.search]
  );

  const [code, setCode] = useState(urlCode);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [autoTried, setAutoTried] = useState(false);

  useEffect(() => {
    if (urlCode && urlCode !== code) setCode(urlCode);
  }, [urlCode]);

  const redeem = async (raw) => {
    const value = (raw || code || '').trim();
    if (!value) {
      setError('Enter your invite code to continue.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await API.post('/invites/redeem', { code: value });
      setInviteUnlock();
      onUnlocked?.();
      if (location.pathname.startsWith('/invite/') || new URLSearchParams(location.search).has('invite')) {
        // /signup?invite=CODE lands on the signup form once the code is accepted.
        // Other params (?redirect=/checkout from checkout's "Create an account") are kept.
        const toSignup = /^\/signup\/?$/i.test(location.pathname);
        const rest = new URLSearchParams(location.search);
        rest.delete('invite');
        const qs = rest.toString();
        navigate(toSignup ? `/signup${qs ? `?${qs}` : ''}` : '/', { replace: true });
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid invite code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (autoTried || !urlCode) return;
    setAutoTried(true);
    redeem(urlCode);
  }, [urlCode, autoTried]);

  return (
    <div
      style={{
        minHeight: '100dvh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#1A1A1A',
        padding: 24,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(ellipse at 30% 40%, rgba(192,57,43,.28) 0%, transparent 55%), radial-gradient(ellipse at 75% 60%, rgba(192,57,43,.14) 0%, transparent 50%)',
          pointerEvents: 'none',
        }}
      />
      <div style={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: 420, textAlign: 'center', color: '#fff' }}>
        <div style={{ fontSize: 56, marginBottom: 12 }} className="float-anim" aria-hidden>🍒</div>
        <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: '#E74C3C', marginBottom: 10 }}>
          Soft launch · Invite only
        </div>
        <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(1.8rem, 5vw, 2.4rem)', fontWeight: 900, lineHeight: 1.15, marginBottom: 12 }}>
          Welcome to CherryPKD
        </h1>
        <p style={{ fontSize: 15, color: 'rgba(255,255,255,.65)', lineHeight: 1.65, marginBottom: 28 }}>
          Rent it. Bid on it. Buy it. Enter your invite code to come in.
        </p>

        <form
          onSubmit={(e) => { e.preventDefault(); redeem(); }}
          style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
        >
          <label htmlFor="invite-code" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
            Invite code
          </label>
          <input
            id="invite-code"
            name="invite"
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
            value={code}
            onChange={(e) => { setCode(e.target.value); if (error) setError(''); }}
            placeholder="Enter invite code"
            disabled={loading}
            style={{
              width: '100%',
              padding: '14px 18px',
              borderRadius: 12,
              border: error ? '1px solid #E74C3C' : '1px solid rgba(255,255,255,.18)',
              background: 'rgba(255,255,255,.08)',
              color: '#fff',
              fontSize: 16,
              fontWeight: 600,
              letterSpacing: 1,
              outline: 'none',
              textAlign: 'center',
            }}
          />
          {error && (
            <div role="alert" style={{ color: '#FF8A80', fontSize: 13, fontWeight: 600 }}>
              {error}
            </div>
          )}
          <button type="submit" className="btn btn-cherry btn-lg" disabled={loading} style={{ width: '100%', justifyContent: 'center' }}>
            {loading ? 'Checking…' : 'Continue'}
          </button>
        </form>

        <p style={{ marginTop: 18, fontSize: 14, color: 'rgba(255,255,255,.7)', lineHeight: 1.5 }}>
          Already have an account?{' '}
          <a
            href="/login"
            onClick={(e) => { e.preventDefault(); navigate('/login'); }}
            style={{ color: '#fff', fontWeight: 700, textDecoration: 'underline', ...FOOTER_TAP, padding: '0 6px' }}
          >
            Log in
          </a>
        </p>
        <p style={{ marginTop: 22, fontSize: 14, color: HELPER_COLOR, lineHeight: 1.55 }}>
          Got an email invite? Use the code in the message, or open the link we sent you.
        </p>
        <p style={{ marginTop: 10, fontSize: 14, color: HELPER_COLOR, lineHeight: 1.55 }}>
          No code yet?{' '}
          <a
            href={`mailto:${INVITE_EMAIL}?subject=${encodeURIComponent('CherryPKD invite request')}`}
            style={{ color: '#fff', fontWeight: 600, textDecoration: 'underline' }}
          >
            Request an invite
          </a>
          {' '}or email <span style={{ color: '#fff', fontWeight: 600, userSelect: 'all', overflowWrap: 'anywhere' }}>{INVITE_EMAIL}</span>
        </p>

        <div style={{ marginTop: 26, display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap', fontSize: 12 }}>
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              style={{ background: 'none', border: 'none', padding: 0, color: HELPER_COLOR, fontSize: 12, fontWeight: 600, cursor: 'pointer', ...FOOTER_TAP }}
            >
              ← About CherryPKD
            </button>
          )}
          {[['Terms', '/terms'], ['Privacy', '/privacy'], ['Refunds', '/refunds']].map(([label, href]) => (
            <a key={href} href={href} style={{ color: HELPER_COLOR, textDecoration: 'none', ...FOOTER_TAP }}>{label}</a>
          ))}
        </div>
      </div>
    </div>
  );
}
